import { Button, Grid, Typography } from '@mui/material';
import { toNumber } from 'lodash';
import {
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CustomButton } from 'src/components/button/Button';
import { FilterList } from 'src/components/filterList';
import { ActionBar, Header } from 'src/components/header';
import { Input } from 'src/components/input';
import { SimpleDialog } from 'src/components/modal';
import { NoResultAlert } from 'src/components/noResultAlert';
import { Progress } from 'src/components/progress/Progress';
import { Snackbar } from 'src/components/snackbar';
import { StyledTypography } from 'src/components/subHeader/SubHeaderWithSorting.styled';
import { COLLECTIONS, USER_ACTIONS } from 'src/constants/constants';
import { PATHS } from 'src/constants/paths';
import {
  useDeleteProductCategory,
  useFilterAndSorting,
  useGetCCAssets,
  useModal,
  useUpdateProductCategoryName,
  useUpdateProductsCategory,
} from 'src/hooks';
import { narrativeGenerators } from 'src/hooks/api/userActions/helper';
import { useNavigateBlocker } from 'src/hooks/navigation';
import { IFilter, IProduct } from 'src/interfaces';
import { lang, useUserRole } from 'src/utils';

import { ManageCategoryLink } from '../addProductCategoryPage/AddProductCategoryPage.styled';
import {
  ButtonContainer,
  InputContainer,
  ProductCategorySectionHeader,
  ProductContainer,
  StyledGrid,
  StyledHeader,
  StyleDivider,
} from './EditProductCategoryPage.styled';
import { useEditProductCategoryPage } from './hooks/EditProductCategoryPage.hook';
import {
  areAllCategorySelected,
  getEditedProducts,
} from './modules/EditProductCategoryPage.helper';
import { ProductGrid } from './ProductGrid';

export const EditProductCategoryPage = () => {
  const params = useParams();
  const { productCategoryId = '' } = params;

  const {
    productCardData,
    allProducts,
    productCategory,
    productVariationTypes,
    productCardLoading,
    productCategoryLoading,
    selectedProductsLoading,
    selectedProductsData,
    currentProductCategory,
    onChangeParams,
    productCategories,
    isDeleted,
  } = useEditProductCategoryPage(productCategoryId);

  const { data: assets = [] } = useGetCCAssets();
  const updateProductCategoryName = useUpdateProductCategoryName();
  const updateProductCategory = useUpdateProductsCategory();
  const [inputBoxStatus, setInputBoxStatus] = useState('null');
  const [selectAll, setSelectAll] = useState(false);
  const [productCategoryName, setProductCategoryName] = useState('');
  const [selectedProducts, setSelectedProducts] = useState<IProduct[]>([]);

  const deleteProductCategory = useDeleteProductCategory();
  const { isViewOnly } = useUserRole();
  const selectedCount = selectedProducts.length;

  useEffect(() => {
    setSelectedProducts(selectedProductsData);

    if (currentProductCategory) {
      setProductCategoryName(currentProductCategory.name);
    }
  }, [selectedProductsData, currentProductCategory]);

  useEffect(() => {
    if (productCardData.length) {
      setSelectAll(areAllCategorySelected(productCardData, selectedProducts));
    }
  }, [productCardData, selectedProducts]);

  const navigate = useNavigate();
  const skipBlockRef = useRef(false);

  const skipNextBlock = useCallback(() => {
    skipBlockRef.current = true;
  }, []);

  const filters: IFilter[] = useMemo(
    () => [
      {
        label: 'Product category',
        options: productCategories.map((item) => ({
          label: item.name,
          value: item.id,
        })),
        isLoading: false,
        customCta: (
          <ManageCategoryLink
            component="button"
            variant="body2"
            onClick={() => navigate(PATHS.PRODUCT_CATEGORIES_MANAGE)}>
            {lang.manageCategory}
          </ManageCategoryLink>
        ),
      },
      {
        label: 'Status',
        options: [
          { label: 'Active', value: 1 },
          { label: 'Inactive', value: 0 },
        ],
        isLoading: false,
      },
      {
        label: 'Variation types',
        options: productVariationTypes.map((item) => ({
          label: item.name || 'None',
          value: item.id,
        })),
        isLoading: false,
      },
      {
        label: 'Components',
        options: [
          { label: ' Assigned to component', value: 'true' },
          { label: ' Unassigned to component', value: 'false' },
        ],
        isLoading: false,
      },
    ],
    [productCategories, productVariationTypes, navigate],
  );

  const { onChangeKeyword, onRemoveKeyword, onReset } =
    useFilterAndSorting(filters);

  const selectAllState = useMemo(
    () => areAllCategorySelected(productCardData, selectedProducts),
    [productCardData, selectedProducts],
  );

  useEffect(() => {
    setSelectAll(selectAllState);
  }, [selectAllState]);

  const initialProductCodesRef = useRef<Set<string>>(new Set());
  const initialCategoryNameRef = useRef<string>('');
  const lastCategoryIdRef = useRef<string>('');
  const lastProductCodesStringRef = useRef<string>('');

  useEffect(() => {
    if (productCategoryId !== lastCategoryIdRef.current) {
      lastCategoryIdRef.current = productCategoryId;
      initialProductCodesRef.current = new Set();
      initialCategoryNameRef.current = '';
      lastProductCodesStringRef.current = '';
    }

    if (selectedProductsData.length > 0) {
      const currentProductCodesString = selectedProductsData
        .map((p) => p.product_code)
        .sort()
        .join(',');

      if (currentProductCodesString !== lastProductCodesStringRef.current) {
        lastProductCodesStringRef.current = currentProductCodesString;
        initialProductCodesRef.current = new Set(
          selectedProductsData.map((p) => p.product_code),
        );
      }
    }
  }, [productCategoryId, selectedProductsData]);

  useEffect(() => {
    if (currentProductCategory?.name) {
      if (
        productCategoryId !== lastCategoryIdRef.current ||
        !initialCategoryNameRef.current
      ) {
        initialCategoryNameRef.current = currentProductCategory.name;
        setProductCategoryName(currentProductCategory.name);
      }
    }
  }, [currentProductCategory?.name, productCategoryId]);

  useEffect(() => {
    setSelectedProducts(selectedProductsData);
  }, [selectedProductsData]);

  const hasCheckboxesChanged = useMemo(() => {
    const currentProductCodes = new Set(
      selectedProducts.map((selectedProduct) => selectedProduct.product_code),
    );
    const initialProductCodes = initialProductCodesRef.current;

    if (currentProductCodes.size !== initialProductCodes.size) {
      return true;
    }

    return !Array.from(currentProductCodes).every((code) =>
      initialProductCodes.has(code),
    );
  }, [selectedProducts]);

  const hasNameChanged = useMemo(() => {
    const trimmedName = productCategoryName.trim();
    const initialName = initialCategoryNameRef.current.trim();

    return (
      trimmedName !== '' &&
      trimmedName !== initialName &&
      inputBoxStatus !== 'error'
    );
  }, [productCategoryName, inputBoxStatus]);
  const discardModal = useModal();
  const deleteModal = useModal();
  const saveModal = useModal();

  const handleCategoryNameChange = useCallback(
    (value: string) => {
      const trimmedValue = value.trim();
      const baseline = (currentProductCategory?.name ?? '').trim();
      startTransition(() => {
        setProductCategoryName(value);

        if (trimmedValue === baseline || trimmedValue === '') {
          setInputBoxStatus('');
        } else {
          setInputBoxStatus('changed');
        }
      });
    },
    [currentProductCategory?.name],
  );

  const handleProductCheckboxChange = useCallback((product: IProduct) => {
    setSelectedProducts((prevSelected) => {
      const isSelected = prevSelected.some(
        (selectedProduct) =>
          selectedProduct.product_code === product.product_code,
      );

      return isSelected
        ? prevSelected.filter(
            (selectedProduct) =>
              selectedProduct.product_code !== product.product_code,
          )
        : [...prevSelected, product];
    });
  }, []);

  const handleSelectAllClick = useCallback(() => {
    if (selectAll) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(productCardData);
    }
  }, [selectAll, productCardData]);

  const handleModalConfirm = useCallback(() => {
    skipNextBlock();
    navigate(PATHS.PRODUCT_CATEGORIES_MANAGE);
  }, [navigate, skipNextBlock]);

  const handleDeleteProductCategory = useCallback(async () => {
    try {
      isDeleted.current = true;
      await deleteProductCategory.mutateAsync(toNumber(productCategoryId));
      skipNextBlock();
      navigate(PATHS.PRODUCT_CATEGORIES_MANAGE);
      Snackbar.success(`${productCategory?.name} ${lang.successfullyDeleted}`);
      deleteModal.onCloseModal();
    } catch {
      isDeleted.current = false;
      Snackbar.error(lang.failedToDeleteCategory);
    }
  }, [
    deleteProductCategory,
    productCategoryId,
    productCategory?.name,
    navigate,
    deleteModal,
    isDeleted,
    skipNextBlock,
  ]);

  const processedAssetPayload = useMemo(() => {
    if (!selectedProducts.length) return [];

    const uniqueAssetsMap = new Map<
      string,
      { id: string; name: string; theme_id: number[] }
    >();

    selectedProducts.forEach((product) => {
      (product.assets || []).forEach((asset) => {
        if (!uniqueAssetsMap.has(asset.id.toString())) {
          uniqueAssetsMap.set(asset.id.toString(), {
            id: asset.id.toString(),
            name: asset.name || '',
            theme_id: asset.theme_id || [],
          });
        }
      });
    });

    return Array.from(uniqueAssetsMap.values()).flatMap((asset) => {
      const existingAsset = assets.find((a) => a.id === parseInt(asset.id));
      const assetName =
        asset.name?.trim() || existingAsset?.name?.trim() || asset.id;

      const newNarrative = (
        narrativeGenerators[COLLECTIONS.ASSETS][USER_ACTIONS.UPDATE] as (
          assetName: string,
        ) => string
      )(assetName);

      const themeIdUnchanged =
        JSON.stringify(existingAsset?.theme_id ?? []) ===
        JSON.stringify(asset.theme_id);
      const narrativeUnchanged =
        (existingAsset as { narrative?: string })?.narrative === newNarrative;

      if (themeIdUnchanged && narrativeUnchanged) {
        return [];
      }

      return [
        {
          resource: COLLECTIONS.ASSETS,
          method: 'PATCH',
          id: asset.id,
          data: {
            theme_id: asset.theme_id,
            narrative: newNarrative,
          },
        },
      ];
    });
  }, [selectedProducts, assets]);

  const handleDone = useCallback(async () => {
    const categoryNameForSave = productCategoryName.trim();

    try {
      if (hasNameChanged) {
        const request = {
          categoryId: productCategoryId,
          categoryName: categoryNameForSave,
        };
        await updateProductCategoryName.mutateAsync(request);
      }

      const editedProducts = getEditedProducts(
        selectedProducts,
        selectedProductsData,
        allProducts,
      );

      const productPayload = editedProducts.map((product) => {
        const isCategoryAlreadyPresent = product.product_category_id.includes(
          toNumber(productCategoryId),
        );
        const updatedProductCategoryIds = isCategoryAlreadyPresent
          ? product.product_category_id.filter(
              (id: number) => id !== toNumber(productCategoryId),
            )
          : [...product.product_category_id, toNumber(productCategoryId)];

        return {
          resource: COLLECTIONS.PRODUCTS,
          method: 'PATCH',
          id: product.product_code,
          data: {
            product_category_id: updatedProductCategoryIds,
            narrative: (
              narrativeGenerators[COLLECTIONS.PRODUCT_CATEGORIES][
                USER_ACTIONS.UPDATE
              ] as (productCategoryName: string) => string
            )(categoryNameForSave || ''),
          },
        };
      });

      const finalPayload = [...productPayload, ...processedAssetPayload];

      await updateProductCategory.mutateAsync(finalPayload);
      skipNextBlock();
      navigate(PATHS.PRODUCT_CATEGORIES_MANAGE);
      Snackbar.success(`${categoryNameForSave} ${lang.successfullyUpdated}`);
    } catch (error: any) {
      if (error.response?.data?.error?.includes('Duplicate entry')) {
        setInputBoxStatus('error');
      }
      Snackbar.error(lang.failedToUpdateCategory);
    }
  }, [
    hasNameChanged,
    productCategoryName,
    productCategoryId,
    updateProductCategoryName,
    selectedProducts,
    selectedProductsData,
    allProducts,
    processedAssetPayload,
    updateProductCategory,
    navigate,
    skipNextBlock,
  ]);

  const isLoading = useMemo(
    () =>
      productCardLoading || selectedProductsLoading || productCategoryLoading,
    [productCardLoading, selectedProductsLoading, productCategoryLoading],
  );

  const isSaveDisabled = useMemo(
    () => !(hasNameChanged || hasCheckboxesChanged),
    [hasNameChanged, hasCheckboxesChanged],
  );

  useNavigateBlocker(
    () =>
      !skipBlockRef.current &&
      !isSaveDisabled &&
      !updateProductCategoryName.isLoading &&
      !updateProductCategory.isLoading &&
      !deleteProductCategory.isLoading,
  );

  const isDeleteDisabled = useMemo(
    () => Boolean(selectedProductsData?.length > 0 || selectedProductsLoading),
    [selectedProductsData?.length, selectedProductsLoading],
  );

  return (
    <Grid>
      <StyledHeader>
        <Header
          title={productCategory?.name}
          actionBar={
            <ActionBar
              type={lang.editProductsCategory}
              buttonLabel={lang.deleteCategory}
              onHandleClick={deleteModal.onOpenModal}
              disabled={isDeleteDisabled}
            />
          }
          breadcrumbItems={[
            { label: lang.products, link: PATHS.PRODUCTS },
            {
              label: lang.manageCategory,
              link: PATHS.PRODUCT_CATEGORIES_MANAGE,
            },
            { label: lang.edit },
          ]}
        />
      </StyledHeader>

      <Grid container spacing={4}>
        <StyledGrid item xs={12} md={3}>
          <FilterList
            filters={filters}
            onChangeKeyword={onChangeKeyword}
            onRemoveKeyword={onRemoveKeyword}
            onChangeFilter={onChangeParams}
            showReset
            onReset={onReset}
          />
        </StyledGrid>
        <StyledGrid item xs={12} md={9}>
          <Typography variant="h4" component="h2" gutterBottom>
            Edit category
          </Typography>
          <StyleDivider />
          <InputContainer>
            <Input
              label="Product category name"
              onChange={handleCategoryNameChange}
              value={productCategoryName}
              status={inputBoxStatus}
              setInputBoxStatus={setInputBoxStatus}
            />
          </InputContainer>
          {!productCardLoading && productCardData.length >= 0 && (
            <StyledTypography paddingTop={4}>
              {`${lang.Showing} ${productCardData.length}/${
                productCardData.length === 0 ? '0' : allProducts.length
              }`}
            </StyledTypography>
          )}
          <ProductContainer>
            {productCardData.length > 0 && (
              <ProductCategorySectionHeader>
                <Typography
                  variant="h4"
                  component="h2"
                  gutterBottom
                  flexGrow={1}>
                  Select products
                </Typography>
                <Typography
                  variant="body1"
                  color="grey"
                  marginRight={1}
                  marginBottom={0}
                  paddingTop={'6px'}>
                  {`${selectedCount} Selected`}
                </Typography>
                <Button variant="text" onClick={handleSelectAllClick}>
                  {selectAll ? 'Deselect all' : 'Select all'}
                </Button>
              </ProductCategorySectionHeader>
            )}
            <StyleDivider />
            {isLoading ? (
              <Progress />
            ) : productCardData.length === 0 ? (
              <NoResultAlert />
            ) : (
              <ProductGrid
                products={productCardData}
                assets={assets}
                selectedProducts={selectedProducts}
                onProductToggle={handleProductCheckboxChange}
              />
            )}

            {!isViewOnly && (
              <ButtonContainer>
                <CustomButton variant="text" onClick={discardModal.onOpenModal}>
                  {lang.close}
                </CustomButton>
                <Button
                  variant="contained"
                  onClick={saveModal.onOpenModal}
                  disabled={isSaveDisabled}>
                  {lang.save}
                </Button>
              </ButtonContainer>
            )}
          </ProductContainer>
        </StyledGrid>
      </Grid>

      <SimpleDialog
        open={discardModal.open}
        cancelText={lang.cancel}
        confirmText={lang.confirm}
        title={lang.discardModal.title}
        onClose={discardModal.onCloseModal}
        onClickCancel={discardModal.onCloseModal}
        modalSize="sm"
        onClickConfirm={handleModalConfirm}
      />

      <SimpleDialog
        open={saveModal.open}
        cancelText={lang.cancel}
        confirmText={lang.confirm}
        title={lang.titleTextSave}
        subTitle={lang.subtitleTextProducts}
        onClose={saveModal.onCloseModal}
        onClickCancel={saveModal.onCloseModal}
        modalSize="sm"
        onClickConfirm={handleDone}
      />

      <SimpleDialog
        actionColor="error"
        open={deleteModal.open}
        cancelText={lang.cancel}
        confirmText={lang.deleteText}
        title={lang.productCategoryTitleTextDelete}
        onClose={deleteModal.onCloseModal}
        onClickCancel={deleteModal.onCloseModal}
        modalSize="sm"
        disabled={isDeleted.current}
        onClickConfirm={handleDeleteProductCategory}
      />
    </Grid>
  );
};
